import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Bell, X, Check, Video, Calendar, MessageSquare, FileText, AlertCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const ax = axios.create({ baseURL: BACKEND_URL, withCredentials: true });

const ICONS = {
  booking_request: Calendar,
  booking_approved: Check,
  booking_declined: X,
  booking_reminder: Bell,
  recap_ready: FileText,
  staff_message: MessageSquare,
  access_request: AlertCircle,
  class_completed: Check,
  class_missed: AlertCircle,
  generic: Bell,
};

const fmtAgo = (iso) => {
  try {
    const ms = Date.now() - new Date(iso).getTime();
    const m = Math.floor(ms / 60000);
    if (m < 1) return 'now';
    if (m < 60) return `${m}m`;
    if (m < 1440) return `${Math.floor(m / 60)}h`;
    return `${Math.floor(m / 1440)}d`;
  } catch (_e) { return ''; }
};

const useIsMobile = () => {
  const [mob, setMob] = useState(typeof window !== 'undefined' ? window.innerWidth < 640 : false);
  useEffect(() => {
    const h = () => setMob(window.innerWidth < 640);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return mob;
};

const NotificationBell = ({ accent = 'red', testidPrefix = '' }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [busy, setBusy] = useState(false);
  const isMobile = useIsMobile();
  const bellRef = useRef(null);
  const panelRef = useRef(null);
  const [panelPos, setPanelPos] = useState({ top: 64, right: 16 });

  const load = useCallback(async () => {
    try { const r = await ax.get('/api/notifications/me', { params: { limit: 25 } }); setItems(r.data.items || []); setUnread(r.data.unread || 0); }
    catch (_e) {}
  }, []);

  const loadUnreadOnly = useCallback(async () => {
    try { const r = await ax.get('/api/notifications/unread-count'); setUnread(r.data.unread || 0); }
    catch (_e) {}
  }, []);

  useEffect(() => {
    loadUnreadOnly();
    const id = setInterval(loadUnreadOnly, 30000);  // 30s for snappier updates
    return () => clearInterval(id);
  }, [loadUnreadOnly]);

  useEffect(() => { if (open) load(); }, [open, load]);

  // Anchor desktop panel to the bell's bounding rect every time it opens
  useEffect(() => {
    if (!open || isMobile) return;
    const recalc = () => {
      const el = bellRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const right = Math.max(16, window.innerWidth - rect.right);
      setPanelPos({ top: rect.bottom + 8, right });
    };
    recalc();
    window.addEventListener('resize', recalc);
    window.addEventListener('scroll', recalc, true);
    return () => { window.removeEventListener('resize', recalc); window.removeEventListener('scroll', recalc, true); };
  }, [open, isMobile]);

  // Close on outside click (desktop) — drawer overlay handles its own dismiss
  useEffect(() => {
    if (!open || isMobile) return;
    const click = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target) && bellRef.current && !bellRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', click);
    return () => document.removeEventListener('mousedown', click);
  }, [open, isMobile]);

  // Lock body scroll while the drawer is open on mobile
  useEffect(() => {
    if (open && isMobile) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [open, isMobile]);

  const markAll = async () => {
    setBusy(true);
    try { await ax.post('/api/notifications/mark-all-read'); await load(); setUnread(0); }
    finally { setBusy(false); }
  };

  const openItem = async (n) => {
    if (!n.read) {
      try { await ax.post('/api/notifications/mark-read', { ids: [n.id] }); } catch (_e) {}
    }
    setOpen(false);
    if (n.link) {
      if (n.link.startsWith('http')) window.open(n.link, '_blank');
      else navigate(n.link);
    }
    load();
  };

  const list = (
    <>
      {items.length === 0 ? (
        <div className="px-4 py-10 text-center text-[11px] text-gray-400 italic" data-testid={`${testidPrefix}notif-empty`}>No notifications yet.</div>
      ) : (
        items.map((n) => {
          const Icon = ICONS[n.kind] || Bell;
          const showJoin = n.kind === 'booking_reminder' && n.link;
          return (
            <button key={n.id} onClick={() => openItem(n)} data-testid={`${testidPrefix}notif-item-${n.id}`}
              className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-gray-50 flex items-start gap-3 ${n.read ? 'opacity-70' : ''}`}>
              <div className={`h-8 w-8 rounded-lg flex-shrink-0 flex items-center justify-center ${n.read ? 'bg-gray-100 text-gray-500' : 'bg-red-700 text-white'}`}><Icon className="h-4 w-4" /></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className={`text-xs ${n.read ? 'font-medium' : 'font-bold'} text-gray-900 truncate`}>{n.title}</div>
                  <span className="text-[10px] text-gray-400 whitespace-nowrap">{fmtAgo(n.created_at)}</span>
                </div>
                {n.body && <div className="text-[11px] text-gray-600 mt-0.5 line-clamp-2">{n.body}</div>}
                {showJoin && (
                  <span className="inline-flex items-center gap-1 mt-1.5 px-2 py-1 rounded-full bg-[#D3D86F] text-black text-[10px] font-bold uppercase tracking-wider"><Video className="h-3 w-3" /> Join Class</span>
                )}
              </div>
            </button>
          );
        })
      )}
    </>
  );

  const header = (
    <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-gray-100">
      <div className="text-[11px] uppercase tracking-[2px] font-bold text-gray-700">Notifications {unread > 0 && <span className="ml-1 text-red-700">({unread})</span>}</div>
      <div className="flex items-center gap-2">
        <button onClick={markAll} disabled={busy || unread === 0} className="text-[10px] uppercase tracking-wider font-bold text-gray-500 hover:text-red-700 disabled:opacity-40 flex items-center gap-1" data-testid={`${testidPrefix}notif-mark-all`}>
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Mark all read
        </button>
        {isMobile && (
          <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-700" data-testid={`${testidPrefix}notif-close`}><X className="h-4 w-4" /></button>
        )}
      </div>
    </div>
  );

  return (
    <>
      <button ref={bellRef} onClick={() => setOpen((o) => !o)} className={`relative h-9 w-9 rounded-full ${accent === 'gold' ? 'bg-[#D3D86F]/20 hover:bg-[#D3D86F]/30 text-[#D3D86F]' : 'bg-white/10 hover:bg-white/20 text-white'} flex items-center justify-center transition`} data-testid={`${testidPrefix}notif-bell`}>
        <Bell className="h-4 w-4" />
        {unread > 0 && <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-700 text-white text-[10px] font-bold flex items-center justify-center" data-testid={`${testidPrefix}notif-badge`}>{unread > 99 ? '99+' : unread}</span>}
      </button>

      {open && typeof document !== 'undefined' && createPortal(
        isMobile ? (
          <div className="fixed inset-0 z-[9999]" data-testid={`${testidPrefix}notif-drawer-overlay`}>
            <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
            <div ref={panelRef} className="absolute right-0 top-0 bottom-0 w-[88%] max-w-[420px] bg-white text-gray-900 shadow-2xl flex flex-col animate-in slide-in-from-right" data-testid={`${testidPrefix}notif-panel`}>
              {header}
              <div className="flex-1 overflow-y-auto">{list}</div>
            </div>
          </div>
        ) : (
          <div
            ref={panelRef}
            className="fixed z-[9999] w-[360px] sm:w-[420px] max-h-[480px] overflow-hidden bg-white text-gray-900 rounded-2xl shadow-2xl border border-gray-200 flex flex-col"
            style={{ top: panelPos.top, right: panelPos.right }}
            data-testid={`${testidPrefix}notif-panel`}
          >
            {header}
            <div className="flex-1 overflow-y-auto">{list}</div>
          </div>
        ),
        document.body,
      )}
    </>
  );
};

export default NotificationBell;
